import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ServiceHero from "../../components/ServiceHero";
import SidebarForm from "../../components/SidebarForm";
import { EprCertificateContainer } from "./EprCertificate.styles.js";

const Section19 = () => {
  return (
    <>
      <Header />
      <ServiceHero
        title="Section 19 – Legal Metrology"
        subtitle="Compliance support for declarations, packaging standards and mandatory requirements under Legal Metrology Packaged Commodities (LMPC) Rules."
        breadcrumb="Section 19 Compliance"
      />
      <EprCertificateContainer>
        <section className="epr-section" aria-labelledby="epr-heading">
          <div className="epr-container">
            <article className="epr-main">
              <h1 id="epr-heading">
                Section 19 – Legal Metrology (Packaged Commodities) Compliance
              </h1>
              <div className="byline">
                Advisory and operational support for LMPC compliance, packaging standards, importer obligations and statutory declarations.
              </div>

         

              <p>
                Section 19 of the Legal Metrology Act, 2009 mandates that no person
                shall manufacture, pack, sell, import or distribute any pre-packaged
                commodity without complying with the prescribed declarations. We help
                businesses meet all requirements under the Legal Metrology (Packaged
                Commodities) Rules, including label compliance, LMPC certificate for
                importers and packaging audits.
              </p>

              <h2>Scope & Applicability</h2>
              <p>
                These rules apply to all pre-packaged commodities intended for retail
                sale — whether manufactured in India or imported. Compliance is
                mandatory for importers, manufacturers, packers, e-commerce sellers,
                warehouses and brand owners dealing in packaged goods.
              </p>

              <h2>Key LMPC Compliance Areas</h2>
              <div className="feature-grid">
                <div className="feature-card small-animate">
                  <i className="fas fa-recycle" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Mandatory Declarations</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Name & address, month/year of packing, Net quantity, MRP, customer
                    care, and other statutory declarations.
                  </p>
                </div>

                <div className="feature-card small-animate">
                  <i className="fas fa-bolt" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Importer LMPC Certificate</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Mandatory registration for companies importing pre-packaged
                    commodities for sale in India.
                  </p>
                </div>

                <div className="feature-card small-animate">
                  <i className="fas fa-microchip" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Label Verification</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Checking compliance with LMPC rules, font size, placement,
                    tamper-proof packaging and MRP accuracy.
                  </p>
                </div>

                <div className="feature-card small-animate">
                  <i className="fas fa-oil-can" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Packaging & Quantity Control</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Ensuring standard packs, net quantity accuracy and manufacturing
                    tolerance as per Legal Metrology norms.
                  </p>
                </div>
              </div>

              <h2>LMPC Registration Process</h2>
              <ol>
                <li>Determine whether you are a Manufacturer / Importer / Packer.</li>
                <li>Collect business details, GST, importer records and packaging samples.</li>
                <li>Apply for LMPC certificate with supporting documents.</li>
                <li>Submit packaging declarations and label samples for approval.</li>
                <li>Receive certificate and maintain records for inspections.</li>
              </ol>

              <h2>Documentation Required</h2>
              <ul>
                <li>Business registration, GST, Import-Export Code</li>
                <li>Packaging label sample with full declarations</li>
                <li>Address proof of premises</li>
                <li>Purchase/import invoices or product list</li>
                <li>Authorization letter & undertaking</li>
              </ul>

              <div style={{ textAlign: "center", margin: "22px 0" }}>
                <svg width="100%" height="36" viewBox="0 0 1200 36" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="g-epr" x1="0" x2="1">
                      <stop offset="0%" stopColor="#1C2951" stopOpacity="0.12" />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.06" />
                    </linearGradient>
                  </defs>
                  <rect width="1200" height="36" fill="url(#g-epr)" rx="6" />
                </svg>
              </div>

              <h2>Common Compliance Requirements</h2>
              <p>Every packaged commodity must display all mandatory declarations accurately:</p>
              <div className="highlight-box">
                <p>
                  <strong>Example:</strong> MRP must be "inclusive of all taxes". Net
                  quantity must match actual content. Incorrect declarations can lead to
                  penalties under Section 36.
                </p>
              </div>

              <h2>Inspection & Verification</h2>
              <p>
                Legal Metrology officers may verify MRP, declarations, net quantity,
                packaging quality and importer certificate. We support clients during
                inspections, audits and documentation checks.
              </p>

              <h2>Penalties & Non-Compliance</h2>
              <p>
                Wrong declarations, incorrect MRP, misleading packaging or selling
                without LMPC certificate can attract penalties under Section 36(1)
                and seizure of goods. We assist in drafting replies and mitigating
                compliance risks.
              </p>

              <h2>Fees & Service Packages</h2>
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Indicative Fee (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>LMPC Importer Certificate</td>
                    <td>₹4,999 — ₹14,999</td>
                  </tr>
                  <tr>
                    <td>Label Review & Packaging Compliance</td>
                    <td>₹2,999 — ₹12,999</td>
                  </tr>
                  <tr>
                    <td>Factory / Warehouse Audit</td>
                    <td>₹9,999 — ₹29,999</td>
                  </tr>
                  <tr>
                    <td>Notice Reply & Legal Support</td>
                    <td>₹5,999 — ₹24,999</td>
                  </tr>
                </tbody>
              </table>

              <h2>Case Studies</h2>
              <p>
                <strong>Importer of Electronics:</strong> Obtained LMPC certificate and corrected packaging labels to pass customs checks.
                <br />
                <strong>Food Brand:</strong> Updated declarations and prevented penalties during market inspection.
              </p>

              <h2>FAQs</h2>
              <div className="highlight-box">
                <h3>Is LMPC mandatory for all importers?</h3>
                <p>Yes, any importer bringing pre-packaged goods must obtain LMPC certificate.</p>
              </div>
              <div className="highlight-box">
                <h3>Can products be sold without declarations?</h3>
                <p>No. Missing or incorrect declarations is an offence under Section 19.</p>
              </div>

              <div style={{ textAlign: "center", marginTop: "18px" }}>
                <a
                  href="#epr-contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("epr-contact").scrollIntoView({ behavior: "smooth" });
                  }}
                  className="btn-primary"
                  style={{ maxWidth: "300px", display: "inline-block" }}
                >
                  Request LMPC Compliance Help
                </a>
              </div>
            </article>

            <aside className="epr-sidebar">
              <SidebarForm />
            </aside>
          </div>
        </section>
      </EprCertificateContainer>
      <Footer />
    </>
  );
};

export default Section19;
